import React, { useCallback } from 'react';
import {
	clipboardEntriesAddById,
	entriesUpdateMany,
	selectClipboardEntries,
	clipboardEntriesResetState,
} from '../containers/contentSlice';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../hooks';
import { Badge, Fab } from '@mui/material';
import ContentPasteGoIcon from '@mui/icons-material/ContentPasteGo';
import type { CanvasPosition, Entry } from '../types';
import { selectLocationId } from '../containers/contentSlice';
import { selectIsAuthenticated } from '../containers/authSlice';
import { useEntryUpdateMutation } from '../services/records';

/** Canvas placement marker in world space; pasted centroid aligns to it when set. */
export type CanvasPlacementMarkerWorld = CanvasPosition | null;

/** World point used as the paste landing target (e.g. canvas center or placement marker). */
export type PasteTargetWorld = { x: number; y: number };

function readEntryCanvasPosition(entry: Entry): CanvasPosition | null {
	const xy = (entry as { xy?: CanvasPosition }).xy;
	if (xy == null || typeof xy.x !== 'number' || typeof xy.y !== 'number') return null;
	if (!Number.isFinite(xy.x) || !Number.isFinite(xy.y)) return null;
	return { x: xy.x, y: xy.y };
}

/** Average of all finite `xy` positions on the given entries; null if none. */
function centroidOfCanvasPositions(entries: Entry[]): PasteTargetWorld | null {
	let sx = 0;
	let sy = 0;
	let n = 0;
	for (const e of entries) {
		const p = readEntryCanvasPosition(e);
		if (!p) continue;
		sx += p.x;
		sy += p.y;
		n += 1;
	}
	if (n === 0) return null;
	return { x: sx / n, y: sy / n };
}

/**
 * Copies clipboard entries into `nextParentId`, shifting every `xy` by the same delta so the
 * group's centroid lands on `targetWorld`. Entries without `xy` are unchanged except `parentId`.
 */
export function buildPastedEntriesWithNormalizedPositions(
	clipboardEntries: Entry[],
	nextParentId: string | null,
	targetWorld: PasteTargetWorld,
): Entry[] {
	const centroid = centroidOfCanvasPositions(clipboardEntries);
	return clipboardEntries.map((clipboardEntry) => {
		const next = { ...clipboardEntry, parentId: nextParentId } as Entry;
		if (!centroid) return next;
		const xy = readEntryCanvasPosition(clipboardEntry);
		if (!xy) return next;
		return {
			...next,
			xy: {
				x: xy.x + (targetWorld.x - centroid.x),
				y: xy.y + (targetWorld.y - centroid.y),
			},
		};
	});
}

export type UseClipboardOptions = {
	/**
	 * When set, pasted centroid lands on this marker; otherwise on canvas world origin.
	 */
	markerWorld?: CanvasPlacementMarkerWorld;
};

export function useClipboard(options: UseClipboardOptions = {}) {
	const { markerWorld } = options;
	const dispatch = useDispatch();
	const clipboardEntries = useAppSelector(selectClipboardEntries);
	const locationId = useAppSelector(selectLocationId);
	const isAuthenticated = useAppSelector(selectIsAuthenticated);
	const [entryUpdateMutation] = useEntryUpdateMutation();

	const cutEntry = useCallback((idOrIds: string | readonly string[]) => {
		const ids = typeof idOrIds === 'string' ? [idOrIds] : idOrIds;
		Array.from(new Set(ids)).forEach((id) => {
			dispatch(clipboardEntriesAddById(id));
		});
	}, [dispatch]);

	const pasteEntries = useCallback(() => {
		const target: PasteTargetWorld = markerWorld ?? { x: 0, y: 0 };
		const updates = buildPastedEntriesWithNormalizedPositions(
			clipboardEntries as Entry[],
			locationId,
			target,
		);
		if (isAuthenticated) {
			entryUpdateMutation(updates);
		} else {
			dispatch(entriesUpdateMany(updates));
		}
		dispatch(clipboardEntriesResetState());
	}, [
		clipboardEntries,
		dispatch,
		entryUpdateMutation,
		isAuthenticated,
		locationId,
		markerWorld,
	]);

	return { cutEntry, pasteEntries, clipboardEntries };
}

type ClipboardActionsFabProps = {
	markerWorld?: CanvasPlacementMarkerWorld;
};

const ClipboardActionsFab = ({ markerWorld }: ClipboardActionsFabProps) => {
	const { clipboardEntries, pasteEntries } = useClipboard({ markerWorld });
	return (
		<>
			{clipboardEntries.length > 0 && (
				<Fab
					size="small"
					color="primary"
					aria-label="scroll back to top"
					onClick={() => pasteEntries()}
				>
					<Badge badgeContent={clipboardEntries.length} color="error">
						<ContentPasteGoIcon />
					</Badge>
				</Fab>
			)}
		</>
	);
};

export default ClipboardActionsFab;
